import React, { useEffect, useState } from 'react';
import { getAttendance, getEmployees, getLeaders, getAttendanceBySearch } from '../../http';
import Loading from '../Loading';

const AttendanceView = () => {
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [employeeMap, setEmployeeMap] = useState({});
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [resetData, setResetData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const attendanceRes = await getAttendance({});
        const employeeRes = await getEmployees();
        const leaderRes = await getLeaders();

        const empMap = {};
        const combinedEmployees = [...employeeRes.data.data, ...leaderRes.data.data];
        combinedEmployees.forEach((emp) => {
          empMap[emp.id] = [emp.name, emp.email];
        });

        setEmployeeMap(empMap);
        setEmployees(combinedEmployees);
        setAttendance(attendanceRes.data.data || []);
        setResetData(attendanceRes.data.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const searchAttendance = async () => {
    const obj = {};

    if (selectedEmployee) obj['employeeID'] = selectedEmployee;
    if (startDate) obj['startDate'] = startDate.toISOString().split('T')[0]; 
    if (endDate) obj['endDate'] = endDate.toISOString().split('T')[0];

    console.log("Search Criteria:", obj);

    try {
      const res = await getAttendanceBySearch(obj);
      setAttendance(res.data.data || []);
      setCurrentPage(1); // Reset to first page on search
    } catch (error) {
      console.error('Error fetching attendance:', error);
      alert('An error occurred while fetching attendance data. Please try again.');
    }
  };

  const resetHandler = () => {
    setSelectedEmployee('');
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setStartDate(null);
    setEndDate(null);
    setAttendance(resetData);
    setCurrentPage(1); // Reset to first page
  };

  // Pagination Logic
  const totalPages = Math.ceil(attendance.length / itemsPerPage);
  const paginatedData = attendance.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const nextPage = () => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  const prevPage = () => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));

  if (isLoading) return <Loading />;

  return (
    <div className="main-content">
      <section className="section">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h4 style={{ fontSize: "24px", color: "black" }}>Attendance</h4>
          </div>
        </div>
        <div className="d-flex justify-content-center w-100">
          <div className="col">
            <label>Select Employee</label>
            <select
              className="form-control select2"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="" disabled>Select an Employee</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>

          <div className='col'>
            <label htmlFor="startDate">Start Date:</label>
            <input 
              type="date" 
              id="startDate" 
              name="startDate" 
              className="form-control"  
              value={startDate ? startDate.toISOString().split('T')[0] : ''} 
              onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
            />
          </div>

          <div className='col'>
            <label htmlFor="endDate">End Date:</label>
            <input 
              type="date" 
              id="endDate" 
              name="endDate" 
              className="form-control"  
              value={endDate ? endDate.toISOString().split('T')[0] : ''} 
              onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
            />
          </div>
          <div className="d-flex flex-row gap-3 mt-4 col">
            <button onClick={searchAttendance} className="btn btn-primary col mr-5">
              Search
            </button>
            <button onClick={resetHandler} className="btn btn-danger col">
              Reset
            </button>
          </div>
        </div>
      </section>

      <div className="table-responsive mt-4">
        <table className="table table-striped table-md center-text">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Date</th>
              <th>Day</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((record, idx) => (
                <tr key={idx}>
                  <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                  <td><b>{employeeMap[record.employeeID]?.[0] || 'Unknown'}</b></td>
                  <td>{employeeMap[record.employeeID]?.[1] || 'Unknown'}</td>
                  <td><b>{`${record.date}/${record.month}/${record.year}`}</b></td>
                  <td>{record.day}</td>
                  <td><b>{record.present ? 'Present' : 'Absent'}</b></td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  No attendance records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <button onClick={prevPage} className="btn btn-primary mx-2" disabled={currentPage === 1}>
            Previous
          </button>
          <span className="align-self-center">Page {currentPage} of {totalPages}</span>
          <button onClick={nextPage} className="btn btn-primary mx-2" disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AttendanceView;